import { useEffect, useRef, useState } from 'react'
import { CheckboxGroupInput } from 'types/input'
import { toggleElementFromArray } from 'utils/helpers'
import Checkbox from './checkbox'
import { FieldValues, Path, PathValue } from 'react-hook-form'

const gridColSpanClass = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
}

/**
 * Custom Text Input element
 * -
 * Need To Pass Following Params
 * - `name` _required_
 * - `setValue` _required_
 * - `defaultSelected` _Optional_
 * - `error` _optional_
 * - `className` _Optional_
 * - `hintText` _optional_
 * - `label` _Optional_
 * - `setTotalSelected` _Optional_ - function to update state variable of selected count be sent to handle total count will set `number`
 */
const CheckboxGroup = <T extends FieldValues>({
  parentClassName = '',
  label,
  name,
  setValue,
  error,
  hintText,
  options,
  setTotalSelected,
  defaultSelected = [],
  includeSelectAll = false,
  displayColumn = 1,
  required,
  reset,
}: CheckboxGroupInput<T>) => {
  // To handle selected options
  const [selected, setSelected] = useState<(string | boolean)[]>([])
  // Ref to handle select all when all value is checked
  const selectAllRef = useRef<HTMLInputElement | null>(null)

  // Will initially fill the state with the dafault value passed
  useEffect(() => {
    const defaultSelectedOptions = defaultSelected?.filter(
      (defaultValue) =>
        options?.some((option) => option.value === defaultValue),
    )
    setSelected(defaultSelectedOptions)
    setValue(name, defaultSelectedOptions as PathValue<T, string & Path<T>>)

    if (includeSelectAll) {
      const enabledValues = options.filter((option) => !option.disabled)
      const allValuesSelected =
        defaultSelectedOptions.length === enabledValues.length

      selectAllRef.current!.checked = allValuesSelected
      selectAllRef.current!.indeterminate =
        !allValuesSelected && defaultSelectedOptions.length > 0
    }

    if (setTotalSelected) {
      setTotalSelected(defaultSelectedOptions.length)
    }
  }, [reset])

  // To handle the selected value and create array of selected value
  const handleInputs = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target

    const selectedValues = toggleElementFromArray({
      value: id,
      array: selected,
    })

    setSelected([...selectedValues])
    setValue(name, selectedValues as PathValue<T, string & Path<T>>)

    // if the state update action is provided then will add with the count
    if (setTotalSelected) {
      setTotalSelected(selectedValues?.length)
    }

    if (includeSelectAll) {
      const enabledValues = options.filter((option) => !option.disabled)
      const allValuesSelected = selectedValues.length === enabledValues.length

      selectAllRef.current!.checked = allValuesSelected
      selectAllRef.current!.indeterminate =
        !allValuesSelected && selectedValues.length > 0
    }
  }

  // To handle Select all
  const selectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    let selectedValues: string[] = []

    if (e.target.checked) {
      selectedValues = options
        .filter((option) => !option?.disabled)
        .map((option) => option.value)
    }

    setSelected(selectedValues)
    setValue(name, selectedValues as PathValue<T, string & Path<T>>)

    // if the state update action is provided then will add with the count
    if (setTotalSelected) {
      setTotalSelected(selectedValues?.length)
    }
  }

  return (
    <div className={`${parentClassName} relative w-full min-w-[150px]`}>
      {/* Label */}
      {label && (
        <label
          htmlFor={name}
          className="mb-2 block text-xs font-semibold text-gray-600"
        >
          {label}
          {required && <span className="text-error">*</span>}
        </label>
      )}
      {/* If 'Select All' Need to include */}
      {includeSelectAll && (
        <div className="mb-4 border-b py-3">
          <Checkbox
            key="select-all"
            id={`select-all-${name}`}
            value={`select-all-${name}`}
            label="Select All"
            onSelect={selectAll}
            CbRef={selectAllRef}
            col={displayColumn}
          />
        </div>
      )}
      {/* Checkbox container */}
      <div className={`my-2 grid gap-4 ${gridColSpanClass[displayColumn]}`}>
        {options?.map((option) => {
          return (
            <Checkbox
              name={option.label}
              label={option.label}
              value={option.value}
              key={option.value}
              checked={selected.includes(option.value)}
              onSelect={handleInputs}
              disabled={option.disabled}
              col={displayColumn}
            />
          )
        })}
      </div>
      {/* Error & Hint Text */}
      {(error || hintText) && (
        <div className="absolute">
          {error ? (
            <p className="mt-1 text-xs text-error">{error}</p>
          ) : (
            <p className="mt-1 px-3 text-[11px] text-gray-500">{hintText}</p>
          )}
        </div>
      )}
    </div>
  )
}
export default CheckboxGroup
